<template>
  <div class="list">
    <!-- 列表视图 -->
    <div class="container" id="container" v-show="curDiary==null">
      <!-- 搜索和筛选区域 -->
      <div class="row mb-3">
        <div class="col-md-10">
          <div class="input-group">
            <span class="input-group-text">关键字</span>
            <input 
              v-model="keyWord" 
              @keyup.enter="handleSearch"
              class="form-control" 
              placeholder="搜索标题或内容"
            />
          </div>
        </div>
        <div class="col-md-2">
          <button class="btn btn-primary form-control" @click="handleSearch">
            搜索
          </button>
        </div>
      </div>

      <!-- 批量操作区域 -->
      <div class="row mb-2" v-if="selectedItems.length > 0">
        <div class="col-md-12">
          <div class="batch-actions">
            <span class="text-muted">已选择 {{selectedItems.length}} 项</span>
            <button class="btn btn-danger btn-sm ms-2" @click="handleBatchDelete">
              批量删除
            </button>
            <button class="btn btn-secondary btn-sm ms-2" @click="clearSelection">
              取消选择
            </button>
          </div>
        </div>
      </div>

      <!-- Loading状态 -->
      <div class="row" v-if="loading">
        <div class="col-md-12 text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">加载中...</span>
          </div>
          <p class="mt-2 text-muted">加载中...</p>
        </div>
      </div>

      <!-- 空状态 -->
      <div class="row" v-if="!loading && diaryArr.length === 0">
        <div class="col-md-12 text-center py-5">
          <div class="empty-state">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="30" stroke="#ccc" stroke-width="2"/>
              <path d="M32 20v24M20 32h24" stroke="#ccc" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <h5 class="mt-3 text-muted">暂无日记</h5>
            <p class="text-muted">{{ keyWord ? '未找到符合条件的记录' : '开始记录您的生活吧' }}</p>
            <router-link to="/" class="btn btn-primary mt-2">
              创建日记
            </router-link>
          </div>
        </div>
      </div>

      <!-- 列表表格 -->
      <div class="row" v-if="!loading && diaryArr.length > 0">
        <div class="col-md-12">
          <div class="table-responsive">
          <table id="DiaryList" class="table table-bordered table-hover table-sm caption-top">
            <caption>共{{totalCount}}条记录，当前显示{{diaryArr.length}}条记录</caption>
            <thead class="table-light">
              <tr>
                <th style="width:40px;">
                  <input 
                    type="checkbox" 
                    v-model="selectAll" 
                    @change="handleSelectAll"
                    :disabled="diaryArr.length === 0"
                  />
                </th>
                <th :style="{width: TABLE_CONFIG.SERIAL_WIDTH}">序号</th>
                <th>内容</th>
                <th :style="{width: TABLE_CONFIG.ACTION_WIDTH}">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(diary, index) in diaryArr" :key="diary.diaryId">
                <td>
                  <input 
                    type="checkbox" 
                    :value="diary.diaryId" 
                    v-model="selectedItems"
                  />
                </td>
                <td>{{getSerialNumber(index)}}</td>
                <td @click="handleDisplay(diary)" class="content-cell">
                  <h6 class="diary-title">
                    <span style="margin-right:10px" v-html="formatTitle(diary.title)"></span>
                    <span class="badge bg-secondary" v-if="diary.tags">{{diary.tags}}</span>
                  </h6>
                  <div class="diary-time">
                    <span v-html="formatDateHtml(diary.addTime)"></span>
                    <span class="mx-1">--</span>
                    <span v-html="formatDateHtml(diary.lastEditTime)"></span>
                  </div>
                  <p class="diary-preview" v-html="formatContent(diary.content)"></p>
                </td>

                <td>
                  <router-link 
                    class="btn btn-info btn-sm" 
                    :to="{path:'/', query:{id:diary.diaryId}}"
                    title="编辑"
                  >
                    编辑
                  </router-link><br/>
                  <button 
                    class="btn btn-warning btn-sm ms-1" 
                    @click="handleDelete(diary.diaryId)"
                    title="删除"
                  >
                    删除
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <!-- 加载更多按钮 -->
      <div class="row" v-if="showMore && !loading">
        <div class="col-md-12 text-center">
          <button class="btn btn-outline-primary form-control" @click="handleLoadMore">
            加载更多
          </button>
        </div>
      </div>
    </div>

    <!-- 详情视图 -->
    <div class="container text-center" v-if="curDiary!=null">
      <DisPlay v-if="curDiary!=null" :diary="curDiary" :keyWord="keyWord"/>
    </div>
  </div>
</template>

<style scoped lang="less">
  table#DiaryList {
    border-radius: 10px;
    width: 100%;
    
    th, td {
      text-align: center;
      vertical-align: middle;
      padding: 10px;
    }
    
    th {
      height: 25px;
      font-size: 16px;
      min-width: 45px;
    }
    
    td {
      .btn {
        margin: 2px;
      }
    }
    
    .content-cell {
      cursor: pointer;
      text-align: left;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: #f8f9fa;
      }
    }
    
    .diary-title {
      margin-bottom: 5px;
      font-weight: 600;
      color: #333;
    }
    
    .diary-time {
      font-size: 12px;
      color: #666;
      margin-bottom: 8px;
    }
    
    .diary-preview {
      margin: 0;
      color: #555;
      line-height: 1.5;
    }
  }

  .empty-state {
    padding: 40px 20px;
    
    svg {
      opacity: 0.5;
    }
    
    h5 {
      font-weight: 600;
    }
  }

  .batch-actions {
    padding: 10px 15px;
    background-color: #f8f9fa;
    border-radius: 5px;
    display: flex;
    align-items: center;
  }

  .mb-3 {
    margin-bottom: 1rem;
  }

  .mb-2 {
    margin-bottom: 0.5rem;
  }

  .ms-2 {
    margin-left: 0.5rem;
  }

  .mx-1 {
    margin-left: 0.25rem;
    margin-right: 0.25rem;
  }

  .mt-2 {
    margin-top: 0.5rem;
  }

  .mt-3 {
    margin-top: 1rem;
  }

  .py-5 {
    padding-top: 3rem;
    padding-bottom: 3rem;
  }

  .spinner-border {
    width: 3rem;
    height: 3rem;
    border-width: 0.25em;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* 响应式表格容器，移动端允许横向滚动但优先尝试换行显示 */
  .table-responsive {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* 限定表格布局，强制单元格换行以适配窄屏 */
  table#DiaryList {
    table-layout: fixed;
  }

  table#DiaryList th,
  table#DiaryList td {
    word-break: break-word;
    white-space: normal;
  }

  /* 手机优化：减小内边距、字体，并限制预览行数 */
  @media (max-width: 576px) {
    table#DiaryList th,
    table#DiaryList td {
      padding: 6px;
      font-size: 13px;
    }

    .diary-title {
      font-size: 14px;
    }

    .diary-preview {
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
</style>

<script>
import DisPlay from '@/components/DisPlay.vue';
import { formatDate } from '@/utils/dateFormat';
import { formatTextForHtml } from '@/utils/textFormat';
import { TABLE_CONFIG, API_ENDPOINTS, TOAST_TYPES, CONFIRM_CONFIG } from '@/utils/constants';
import { debounce } from '@/utils/debounce';

export default {
  name: 'List',
  
  components: { DisPlay },
  
  data() {
    return {
      user: '',
      totalCount: 0,
      beg: 0,
      count: TABLE_CONFIG.PAGE_SIZE,
      showMore: false,
      loading: false,
      keyWord: '',          // 搜索关键字
      diaryArr: [],         // 日记列表
      curDiary: null,       // 当前日记
      selectedItems: [],    // 选中的项目
      selectAll: false,     // 全选状态
      TABLE_CONFIG          // 表格配置常量
    };
  },
  
  methods: {
    /**
     * 获取日记列表
     */
    async getDiaryList(isMore = false) {
      const that = this;
      
      if (!isMore) {
        that.beg = 0;
        that.selectedItems = [];
        that.selectAll = false;
      }
      
      that.loading = true;
      
      try {
        await that.$myAxios(that, {
          method: 'GET',
          url: API_ENDPOINTS.DIARY_LIST,
          params: {
            user: that.user,
            keyword: that.keyWord,
            beg: that.beg,
            count: that.count
          }
        }, (res) => {
          if (!isMore) {
            that.diaryArr = res.description || [];
          } else {
            that.diaryArr.push(...(res.description || []));
          }
          
          const resCount = res.description?.length || 0;
          that.totalCount = res.msg || 0;
          that.beg += resCount;
          
          that.showMore = resCount === that.count;
        });
      } catch (error) {
        console.error('获取日记列表异常:', error);
        that.$toast('加载异常', TOAST_TYPES.ERROR);
      } finally {
        that.loading = false;
      }
    },
    
    /**
     * 根据ID获取单个日记
     */
    async getDiaryById(id) {
      const that = this;
      that.loading = true;
      
      try {
        await that.$myAxios(that, {
          method: 'GET',
          url: API_ENDPOINTS.DIARY_GET_BY_ID,
          params: { id }
        }, (res) => {
          that.curDiary = res.description;
        });
      } catch (error) {
        console.error('获取日记详情异常:', error);
      } finally {
        that.loading = false;
      }
    },
    
    /**
     * 删除日记
     */
    async deleteDiary(id) {
      const that = this;
      
      try {
        await that.$myAxios(that, {
          method: 'POST',
          url: `${API_ENDPOINTS.DIARY_DELETE}?id=${id}`
        }, (res) => {
          that.$toast(res.msg || '删除成功', TOAST_TYPES.SUCCESS);
          that.getDiaryList();
        });
      } catch (error) {
        console.error('删除日记异常:', error);
      }
    },
    
    /**
     * 搜索处理（防抖）
     */
    handleSearch: debounce(function() {
      this.getDiaryList(false);
    }, 300),
    
    /**
     * 加载更多
     */
    handleLoadMore() {
      this.getDiaryList(true);
    },
    
    /**
     * 显示日记详情
     */
    handleDisplay(diary) {
      const routeUrl = this.$router.resolve({
        path: '/list',
        query: { id: diary.diaryId }
      });
      window.open(routeUrl.href, '_blank');
    },
    
    /**
     * 删除日记（带确认）
     */
    handleDelete(id) {
      const confirmed = confirm(CONFIRM_CONFIG.DELETE_DIARY.message);
      if (confirmed) {
        this.deleteDiary(id);
      }
    },
    
    /**
     * 批量删除
     */
    handleBatchDelete() {
      if (this.selectedItems.length === 0) {
        this.$toast('请先选择要删除的项目', TOAST_TYPES.WARNING);
        return;
      }
      
      const confirmed = confirm(
        `${CONFIRM_CONFIG.BATCH_DELETE.message}\n将删除 ${this.selectedItems.length} 条记录。`
      );
      
      if (confirmed) {
        // 这里需要后端支持批量删除接口
        // 暂时使用循环删除
        this.selectedItems.forEach(id => {
          this.deleteDiary(id);
        });
        this.selectedItems = [];
        this.selectAll = false;
      }
    },
    
    /**
     * 全选/取消全选
     */
    handleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = this.diaryArr.map(d => d.diaryId);
      } else {
        this.selectedItems = [];
      }
    },
    
    /**
     * 清除选择
     */
    clearSelection() {
      this.selectedItems = [];
      this.selectAll = false;
    },
    
    /**
     * 获取序号
     */
    getSerialNumber(index) {
      return this.beg - this.diaryArr.length + index + 1;
    },
    
    /**
     * 格式化标题
     */
    formatTitle(title) {
      return formatTextForHtml(title, {
        keyword: this.keyWord,
        limit: 0
      });
    },
    
    /**
     * 格式化内容
     */
    formatContent(content) {
      return formatTextForHtml(content, {
        keyword: this.keyWord,
        limit: TABLE_CONFIG.PREVIEW_LENGTH
      });
    },
    
    /**
     * 格式化日期为HTML
     */
    formatDateHtml(date) {
      return formatDate(date, false);
    }
  },
  
  watch: {
    selectedItems(newVal) {
      this.selectAll = newVal.length === this.diaryArr.length && this.diaryArr.length > 0;
    }
  },
  
  mounted() {
    const user = this.$cookies.get('username');
    
    if (!user) {
      this.$router.push('/login');
      return;
    }
    
    this.user = user;
    
    // 检查是否有id参数，如果有则显示详情
    const diaryId = this.$route.query.id;
    if (diaryId) {
      this.getDiaryById(diaryId);
    } else {
      this.getDiaryList();
    }
  }
};
</script>
